import { ReactNode, useState } from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { Tag, Tooltip, Typography } from "antd";
import { Link } from "react-router";

import { useScopeParams } from "@/hooks/useScopeParams";

import PageHeader from "@/components/UI/PageHeader";

import { formatNumberWithGrouping } from "@/utils/numberFormatting";
import type {
  PortfolioAdditionalStat,
  PortfolioMetricFinalOutcome,
  PortfolioMetricPlanStatus,
  PortfolioMetricView,
  PortfolioStatusColor,
  PortfolioTrialHeaderInfo,
} from "./types";
import {
  describePortfolioMetricFinalOutcome,
  describePortfolioMetricView,
  derivePortfolioMetricProgress,
  formatPortfolioAdditionalStatRange,
  formatPortfolioMetricValue,
} from "./utils";

// eslint-disable-next-line react-refresh/only-export-components -- pre-existing constant colocated with components in this file
export const PORTFOLIO_PROGRESS_BAR_COLORS = {
  blue: "#64BDE1",
  gold: "#BCA048",
  magenta: "#C275A1",
} as const;

export type PortfolioProgressBarTone =
  keyof typeof PORTFOLIO_PROGRESS_BAR_COLORS;

const DELTA_PERIOD_TOOLTIP = "30 days since last ingestion";

const PERCENT_METRIC_SUFFIX = "%";
const PERCENT_METRIC_TOTAL = 100;

export function PortfolioPageShell({
  title,
  tooltipText,
  children,
}: {
  title: string;
  tooltipText: string;
  children: ReactNode;
}) {
  return (
    <div className="w-full p-4">
      <div className="relative z-20 mb-4 flex justify-between">
        <div className="flex gap-2">
          <PageHeader title={title} tooltipText={tooltipText} />
        </div>
      </div>

      {children}
    </div>
  );
}

export function PortfolioCardsList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>;
}

export function PortfolioCardShell({
  header,
  metricColumns,
  additionalColumns,
  initiallyExpanded = true,
  children,
  additionalContent,
}: {
  header: ReactNode;
  metricColumns: number;
  additionalColumns?: number;
  initiallyExpanded?: boolean;
  children: ReactNode;
  additionalContent?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);

  return (
    <section className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      {header}

      <div
        className="grid divide-x divide-[#F0F2F4] border-b border-[#F0F2F4]"
        style={{
          gridTemplateColumns: `repeat(${metricColumns}, minmax(0, 1fr))`,
        }}
      >
        {children}
      </div>

      {expanded && additionalContent ? (
        <div
          className="grid items-stretch divide-x divide-[#F0F2F4] border-b border-[#F0F2F4]"
          style={{
            gridTemplateColumns: `repeat(${additionalColumns ?? metricColumns}, minmax(0, 1fr))`,
          }}
        >
          {additionalContent}
        </div>
      ) : null}

      <PortfolioExpandCollapseFooter
        expanded={expanded}
        onToggle={() => setExpanded((current) => !current)}
      />
    </section>
  );
}

const PORTFOLIO_TRIAL_HEADER_ROW =
  "flex h-[45px] items-center justify-between gap-4 border-b border-[#F0F2F4] px-[18px]";

// Only the linking variant reaches for routing, so headers that do not link --
// and their tests -- stay free of any Router requirement.
function PortfolioTrialHeaderLinkRow({
  trial,
  children,
}: {
  trial: PortfolioTrialHeaderInfo;
  children: ReactNode;
}) {
  const { buildUrl } = useScopeParams();

  return (
    <Link
      to={buildUrl(`trials/${trial.trial_id}`)}
      aria-label={`Open ${trial.name} trial profile`}
      className={`${PORTFOLIO_TRIAL_HEADER_ROW} transition-colors hover:bg-[#F7FAFB]`}
    >
      {children}
    </Link>
  );
}

export function PortfolioTrialHeader({
  trial,
  titleClassName = "!font-semibold",
  statusNode,
  linkToTrial = false,
}: {
  trial: PortfolioTrialHeaderInfo;
  titleClassName?: string;
  statusNode?: ReactNode;
  /** Turns the row into a link to the trial's portfolio profile. */
  linkToTrial?: boolean;
}) {
  const content = (
    <>
      <div className="flex min-w-0 items-center gap-2.5">
        <Typography.Title
          level={5}
          className={`!mb-0 !text-[15px] !leading-5 !text-[#1F2933] ${titleClassName}`}
        >
          {trial.name}
        </Typography.Title>
        <Typography.Text className="text-[13px] leading-4 text-[#8C8C8C]">
          {trial.trial_id}
        </Typography.Text>
        <Tag className="m-0 h-5 rounded-[4px] border-0 bg-[#E8EEF2] px-1.5 py-0 text-[13px] leading-5 text-[#48636F]">
          {trial.phase}
        </Tag>
        <Typography.Text className="text-[13px] leading-4 text-[#6B7280]">
          {trial.asset}
        </Typography.Text>
        <span className="h-1 w-1 rounded-full bg-[#BDBDBD]" />
        <Typography.Text className="truncate text-[13px] leading-4 text-[#8C8C8C]">
          {trial.studyType}
        </Typography.Text>
      </div>
      {statusNode ?? (
        <PortfolioStatusTag status={trial.status} color={trial.statusColor} />
      )}
    </>
  );

  if (!linkToTrial) {
    return <div className={PORTFOLIO_TRIAL_HEADER_ROW}>{content}</div>;
  }

  return (
    <PortfolioTrialHeaderLinkRow trial={trial}>
      {content}
    </PortfolioTrialHeaderLinkRow>
  );
}

export function PortfolioStatusTag({
  status,
  color,
}: {
  status: string;
  color: PortfolioStatusColor;
}) {
  const toneClass =
    color === "green"
      ? "bg-[#CAE6D6] text-[#0D433C]"
      : color === "blue"
        ? "bg-[#D2EFF9] text-[#064C68]"
        : "bg-[#D2EFF9] text-[#064C68]";

  return (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-[12px] font-semibold leading-5 ${toneClass}`}
    >
      {status}
    </span>
  );
}

export function PortfolioEmptyAdditionalStatTile() {
  return (
    <div className="relative h-full min-h-[76px] bg-[#fbfbfc]">
      <div className="absolute inset-0 flex items-center justify-center">
        <Typography.Text className="text-[10px] leading-3 text-[rgba(0,0,0,0.45)]">
          No additional stats
        </Typography.Text>
      </div>
    </div>
  );
}

export function PortfolioAdditionalStatTile({
  stat,
  unit,
}: {
  stat: PortfolioAdditionalStat;
  unit?: string;
}) {
  if (!stat) {
    return <PortfolioEmptyAdditionalStatTile />;
  }

  const [average, deviation] = stat.value.split(" ± ");
  const unitSuffix = unit ? ` ${unit}` : "";
  const rangeText = formatPortfolioAdditionalStatRange(stat);
  const rangeUnitSuffix = rangeText === "—" ? "" : unitSuffix;

  return (
    <div className="flex h-full min-h-[76px] flex-col justify-center bg-[#fbfbfc] px-5 py-3">
      <Typography.Text className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8EA0BF]">
        {stat.label}
      </Typography.Text>
      <div className="mt-1 text-[14px] font-semibold leading-[18px] text-[#102033]">
        {average}
        {deviation ? (
          <span className="text-[11px] font-normal text-[#6B7280]">
            {" "}
            ± {deviation}
          </span>
        ) : null}
        {unitSuffix}
      </div>
      <Typography.Text className="text-[10px] leading-3 text-[#8EA0BF]">
        Range {rangeText}
        {rangeUnitSuffix}
      </Typography.Text>
    </div>
  );
}

const PORTFOLIO_PLAN_STATUS_PILL_STYLES: Record<
  PortfolioMetricPlanStatus,
  string
> = {
  // Ahead/On Plan/Met plan colors are traced to Figma nodes 4139:1114, 4139:1119,
  // 4135:3260, and DECISIONS.md. Behind color confirmed by Pedro's visual QA.
  behind: "bg-[#FFEBEB] text-[#980000]",
  onPlan: "bg-[#DFE0ED] text-[#3D4273]",
  ahead: "bg-[#F0FAF7] text-[#387853]",
  metPlan: "bg-[#D2EFF9] text-[#064C68]",
};

const PORTFOLIO_PLAN_STATUS_PILL_LABELS: Record<
  PortfolioMetricPlanStatus,
  string
> = {
  behind: "Behind",
  onPlan: "On Plan",
  ahead: "Ahead",
  metPlan: "Met plan",
};

export function PortfolioMetricStatusPill({
  status,
}: {
  status: PortfolioMetricPlanStatus | null;
}) {
  if (!status) return null;

  return (
    <span
      className={`inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-full px-2 text-[12px] font-bold leading-none ${PORTFOLIO_PLAN_STATUS_PILL_STYLES[status]}`}
    >
      {PORTFOLIO_PLAN_STATUS_PILL_LABELS[status]}
    </span>
  );
}

export function PortfolioMetricDeltaBadge({
  delta30d,
  invert = false,
}: {
  delta30d: number | null;
  invert?: boolean;
}) {
  if (delta30d === null) return null;

  const isNoChange = delta30d === 0;
  const isSemanticallyPositive = invert ? delta30d < 0 : delta30d > 0;
  const toneClass = isNoChange
    ? "bg-[#F0F2F4] text-[#556C75]"
    : isSemanticallyPositive
      ? "bg-[#F0FAF7] text-[#387853]"
      : "bg-[#FBEAE5] text-[#C2410C]";
  const label = isNoChange
    ? "±0"
    : `${delta30d > 0 ? "+" : ""}${formatNumberWithGrouping(delta30d)}`;

  return (
    <span
      className={`inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-full px-1 text-[11px] font-bold leading-none ${toneClass}`}
    >
      {label}
    </span>
  );
}

function PortfolioProgressFillDivider({ percent }: { percent: number }) {
  if (percent <= 0 || percent >= 100) return null;

  return (
    <div
      className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-white"
      style={{ left: `${percent}%` }}
    />
  );
}

export type PortfolioBarFill = {
  percent: number;
  color: string;
  key?: string;
  className?: string;
};

export function PortfolioMetricBar({
  fills,
  markerPercent,
}: {
  fills: PortfolioBarFill[];
  markerPercent?: number | null;
}) {
  let cumulative = 0;
  const boundaries = fills.map((fill) => ({
    key: fill.key ?? `${fill.color}-${fill.percent}`,
    percent: (cumulative += fill.percent),
  }));

  return (
    <div className="relative mt-1.5 h-[6px] w-full rounded-full bg-[rgba(0,0,0,0.06)]">
      {fills.length > 0 ? (
        <div className="absolute inset-y-0 left-0 flex h-full w-full overflow-hidden rounded-full">
          {fills.map((fill) => (
            <div
              key={fill.key ?? `${fill.color}-${fill.percent}`}
              className={`h-[6px] ${fill.className ?? ""}`}
              style={{
                width: `${fill.percent}%`,
                backgroundColor: fill.color,
              }}
            />
          ))}
        </div>
      ) : null}
      {boundaries.map((boundary) => (
        <PortfolioProgressFillDivider
          key={boundary.key}
          percent={boundary.percent}
        />
      ))}
      {markerPercent != null ? (
        <svg
          className="absolute top-[-4px] -translate-x-1/2"
          style={{ left: `${markerPercent}%` }}
          width="12"
          height="9"
          viewBox="0 0 12 9"
          aria-hidden="true"
        >
          <path
            d="M6 8 L1 1 L11 1 Z"
            fill="#2B4752"
            stroke="#FFFFFF"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </div>
  );
}

export function PortfolioMetricTotalRow({
  total,
  formatValue,
  suffix,
}: {
  total: number;
  formatValue: (value: number) => string;
  suffix?: string;
}) {
  return (
    <div className="flex justify-end gap-1">
      <span className="text-[11px] font-bold leading-none text-[#556C75]">
        Total
      </span>
      <span className="text-[11px] leading-none text-[#556C75]">
        {formatPortfolioMetricValue(total, formatValue)}
        {suffix ?? ""}
      </span>
    </div>
  );
}

export type PortfolioSegment = {
  value: number;
  color: string;
  label: string;
};

function computePortfolioSegmentFills(
  segments: PortfolioSegment[],
): PortfolioBarFill[] {
  const total = segments.reduce((sum, seg) => sum + Math.max(0, seg.value), 0);
  if (total <= 0) return [];
  return segments.map((seg) => ({
    percent: (Math.max(0, seg.value) / total) * 100,
    color: seg.color,
    key: `${seg.label}-${seg.color}`,
  }));
}

export function PortfolioMetricLegend({
  items,
}: {
  items: { label: string; color: string }[];
}) {
  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-medium leading-none text-[#8EA0BF]">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1">
          <span
            className="inline-block h-[5px] w-[5px] rounded-full"
            style={{ backgroundColor: item.color }}
          />
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function PortfolioMetricLegendAndTotalRow({
  legendItems,
  total,
  formatValue,
  suffix,
}: {
  legendItems: { label: string; color: string }[];
  total: number | null;
  formatValue: (value: number) => string;
  suffix?: string;
}) {
  if (legendItems.length === 0 && total === null) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
      <PortfolioMetricLegend items={legendItems} />
      {total !== null ? (
        <PortfolioMetricTotalRow
          total={total}
          formatValue={formatValue}
          suffix={suffix}
        />
      ) : null}
    </div>
  );
}

function PortfolioMetricFinalTag({
  finalOutcome,
  formatValue,
  suffix,
}: {
  finalOutcome: PortfolioMetricFinalOutcome | null;
  formatValue: (value: number) => string;
  suffix?: string;
}) {
  const outcomeText = describePortfolioMetricFinalOutcome(
    finalOutcome,
    formatValue,
    suffix,
  );

  return (
    <span className="inline-flex items-center gap-1 text-[12px] font-medium leading-none">
      <span className="text-[#8C8C8C]">Final</span>
      {outcomeText ? (
        <>
          <span>·</span>
          <span className="inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-sm border border-[#D9DEE1] bg-[#F0F2F4] px-1.5 text-[11px] font-bold leading-none text-[#556C75]">
            {outcomeText}
          </span>
        </>
      ) : null}
    </span>
  );
}

function PortfolioMetricValueRow({
  view,
  formatValue,
  suffix,
}: {
  view: PortfolioMetricView;
  formatValue: (value: number) => string;
  suffix?: string;
}) {
  const showPlanned =
    view.planTracked && view.lifecycle === "active" && view.planned !== null;
  const showTotal = view.lifecycle === "final" && view.total !== null;

  return (
    <div className="flex items-end gap-1 whitespace-nowrap leading-none">
      <span className="text-[20px] font-semibold text-[#0F1923]">
        {formatPortfolioMetricValue(view.actual, formatValue)}
      </span>
      {showPlanned ? (
        <span className="text-[14px] font-normal leading-none text-[#8C8C8C]">
          / {formatPortfolioMetricValue(view.planned, formatValue)} planned
          {view.plannedPeriodLabel ? ` (${view.plannedPeriodLabel})` : ""}
        </span>
      ) : null}
      {showTotal ? (
        <span className="text-[14px] font-normal leading-none text-[#8C8C8C]">
          / {formatPortfolioMetricValue(view.total, formatValue)} total
        </span>
      ) : null}
      {suffix ? (
        <span className="text-[14px] font-normal leading-none text-[#2B4752]">
          {suffix}
        </span>
      ) : null}
    </div>
  );
}

function PortfolioMetricProgressArea({
  view,
  formatValue,
  suffix,
  progressFillColor,
}: {
  view: PortfolioMetricView;
  formatValue: (value: number) => string;
  suffix?: string;
  progressFillColor: string;
}) {
  if (view.lifecycle === "final") {
    return view.actualProgress ? (
      <PortfolioMetricBar
        fills={[
          {
            percent: view.actualProgress.percent,
            color: "#808E94",
            className: "bg-[#808E94]",
          },
        ]}
      />
    ) : null;
  }

  if (!view.actualProgress && view.total === null) return null;

  return (
    <div className="flex flex-col gap-1">
      {view.actualProgress ? (
        <PortfolioMetricBar
          fills={[
            { percent: view.actualProgress.percent, color: progressFillColor },
          ]}
          markerPercent={view.plannedMarkerProgress?.percent ?? null}
        />
      ) : null}
      {view.total !== null ? (
        <PortfolioMetricTotalRow
          total={view.total}
          formatValue={formatValue}
          suffix={suffix}
        />
      ) : null}
    </div>
  );
}

function PortfolioMetricFooterRow({
  view,
  invertDelta,
  formatValue,
  suffix,
}: {
  view: PortfolioMetricView;
  invertDelta: boolean;
  formatValue: (value: number) => string;
  suffix?: string;
}) {
  if (view.lifecycle === "final") {
    return (
      <PortfolioMetricFinalTag
        finalOutcome={view.finalOutcome}
        formatValue={formatValue}
        suffix={suffix}
      />
    );
  }

  if (view.delta30d === null) return null;

  return (
    <Tooltip title={DELTA_PERIOD_TOOLTIP}>
      <div
        className="flex w-fit cursor-help items-center gap-1"
        tabIndex={0}
        aria-label={DELTA_PERIOD_TOOLTIP}
      >
        <PortfolioMetricDeltaBadge
          delta30d={view.delta30d}
          invert={invertDelta}
        />
        <span className="text-[11px] font-normal leading-none text-[rgba(0,0,0,0.35)]">
          in last 30d
        </span>
      </div>
    </Tooltip>
  );
}

export function PortfolioMetricCard({
  label,
  view,
  formatValue = formatNumberWithGrouping,
  suffix,
  invertDelta = false,
  progressTone = "blue",
  segments,
}: {
  label: string;
  view: PortfolioMetricView;
  formatValue?: (value: number) => string;
  suffix?: string;
  invertDelta?: boolean;
  progressTone?: PortfolioProgressBarTone;
  segments?: PortfolioSegment[];
}) {
  // Plan-status pills intentionally hidden pending product decision (Pedro, VIVO-8508) — do not delete, restore by uncommenting.
  // const showStatusPill =
  //   view.planTracked && view.lifecycle === "active" && view.planStatus !== null;
  const isFinal = view.lifecycle === "final";
  const hasSegments = segments !== undefined;
  const segmentsRawTotal = hasSegments
    ? segments.reduce((sum, seg) => sum + Math.max(0, seg.value), 0)
    : 0;
  // Segmented bars are always out of 100 (a share breakdown), so the total is
  // fixed rather than supplied by data. Completed cards drop the total row,
  // mirroring the same choice already made for plain percent metrics below.
  const segmentsHaveTotal = hasSegments && segmentsRawTotal > 0 && !isFinal;

  // A percent metric is always out of 100, so the total is fixed rather than
  // supplied by data, and it drives the bar even when it is not displayed.
  // Completed percent cards show only the value, the bar and Final: no total,
  // and no outcome, since a difference from 100 is not a plan shortfall.
  const effectiveView: PortfolioMetricView = hasSegments
    ? {
        ...view,
        total: segmentsHaveTotal ? PERCENT_METRIC_TOTAL : null,
        finalOutcome: null,
        actualProgress: null,
        plannedMarkerProgress: null,
      }
    : suffix === PERCENT_METRIC_SUFFIX
      ? {
          ...view,
          total: isFinal ? null : PERCENT_METRIC_TOTAL,
          finalOutcome: null,
          actualProgress: derivePortfolioMetricProgress(
            view.actual,
            PERCENT_METRIC_TOTAL,
          ),
        }
      : view;

  const legendItems =
    hasSegments && segmentsRawTotal > 0
      ? segments.map((segment) => ({
          label: segment.label,
          color: segment.color,
        }))
      : [];

  const segmentsAccessibleBreakdown =
    hasSegments && segmentsRawTotal > 0
      ? `, breakdown ${segments
          .map(
            (segment) =>
              `${formatNumberWithGrouping(Math.round((Math.max(0, segment.value) / segmentsRawTotal) * 100))}% ${segment.label}`,
          )
          .join(", ")}`
      : "";

  return (
    <div
      className={`flex min-h-[96px] flex-col gap-[6px] px-[17px] py-3 ${isFinal ? "bg-[#FBFBFC]" : ""}`}
    >
      <span className="sr-only">
        {describePortfolioMetricView(effectiveView, label, formatValue, suffix)}
        {segmentsAccessibleBreakdown}
      </span>
      <div aria-hidden="true" className="flex flex-1 flex-col gap-[6px]">
        <div className="flex items-start justify-between gap-2">
          <span className="block truncate text-[12px] font-medium uppercase leading-none tracking-[-0.12px] text-[#A5A5A5]">
            {label}
          </span>
          {/* {showStatusPill ? (
            <PortfolioMetricStatusPill status={view.planStatus} />
          ) : null} */}
        </div>
        <PortfolioMetricValueRow
          view={effectiveView}
          formatValue={formatValue}
          suffix={suffix}
        />
        {hasSegments ? (
          <PortfolioMetricBar fills={computePortfolioSegmentFills(segments)} />
        ) : (
          <PortfolioMetricProgressArea
            view={effectiveView}
            formatValue={formatValue}
            suffix={suffix}
            progressFillColor={PORTFOLIO_PROGRESS_BAR_COLORS[progressTone]}
          />
        )}
        {hasSegments ? (
          <PortfolioMetricLegendAndTotalRow
            legendItems={legendItems}
            total={segmentsHaveTotal ? PERCENT_METRIC_TOTAL : null}
            formatValue={formatNumberWithGrouping}
            suffix={PERCENT_METRIC_SUFFIX}
          />
        ) : null}
        <div className="mt-auto">
          <PortfolioMetricFooterRow
            view={effectiveView}
            invertDelta={invertDelta}
            formatValue={formatValue}
            suffix={suffix}
          />
        </div>
      </div>
    </div>
  );
}

function PortfolioExpandCollapseFooter({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex h-6 items-center justify-center">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1 text-[12px] font-medium leading-5 text-[#8EA0BF] hover:text-[#5F789C]"
      >
        {expanded ? (
          <UpOutlined className="text-[10px]" />
        ) : (
          <DownOutlined className="text-[10px]" />
        )}
        {expanded ? "Hide statistics" : "Show statistics"}
      </button>
    </div>
  );
}
